import { parseProtocolMarkdown, ParsedProtocol, ProtocolParticipant, ProtocolSpeech } from './parse-protocol';
import { isBadgeRoleLabel, isJobTitleRole, resolveSpeakerRole } from './speaker-roles';
import { canonicalizeSpeakerDisplayName } from './speaker-display-names';
import { speakerNamesMatch } from '../pages/files/utils/audio-segments';

export type KtzhProtocolDocument = {
  meetingTitle: string;
  chairTitle: string;
  chairName: string;
  city: string;
  protocolNumber: string;
  dateLabel: string;
  topicLine: string;
  established: string[];
  decisionGroups: { header: string; items: string[] }[];
  signatureTitle: string;
  signatureName: string;
  participants: { num: number; name: string; position: string }[];
};

const MONTHS_RU = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
];

const BADGE_ONLY = new Set(['участник', 'руководитель', 'сотрудник ттк', 'секретарь', 'докладчик']);

function isPlaceholderText(value: string): boolean {
  const v = (value || '').trim();
  if (!v) return true;
  return /^\[.+\]$|^_{2,}$|подписан/i.test(v);
}

export function formatDateKtzh(dateRaw: string): string {
  const raw = (dateRaw || '').trim();
  if (!raw || isPlaceholderText(raw)) return '';

  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    const day = Number(iso[3]);
    const month = Number(iso[2]);
    const year = Number(iso[1]);
    return `${String(day).padStart(2, '0')} ${MONTHS_RU[month - 1]} ${year} года`;
  }

  const dmy = raw.match(/^(\d{1,2})[.\-/](\d{1,2})[.\-/](\d{4})/);
  if (dmy) {
    const day = Number(dmy[1]);
    const month = Number(dmy[2]);
    const year = Number(dmy[3]);
    return `${String(day).padStart(2, '0')} ${MONTHS_RU[month - 1]} ${year} года`;
  }

  const ru = raw.match(/^(\d{1,2})\s+([а-яё]+)\s+(\d{4})/i);
  if (ru) {
    return `${String(Number(ru[1])).padStart(2, '0')} ${ru[2].toLowerCase()} ${ru[3]} года`;
  }

  return isPlaceholderText(raw) ? '' : raw;
}

function extractProtocolNumber(protocol: string, parsed: ParsedProtocol): string {
  const fromTitle = (parsed.title || '').match(/№\s*(\d+)/i);
  if (fromTitle) return fromTitle[1];
  const fromBody = protocol.match(/протокол[^\n]*№\s*(\d+)/i);
  if (fromBody) return fromBody[1];
  return '____';
}

function shortName(fullName: string): string {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '';
  if (parts.length === 1) return parts[0];
  const initials = parts.slice(1).map(p => `${p[0]}.`).join('');
  return `${parts[0]} ${initials}`.trim();
}

function normalizeNameKey(name: string): string {
  return name.toLowerCase().replace(/[^\p{L}\d\s]/gu, '').replace(/\s+/g, ' ').trim();
}

function displaySpeakerName(name: string): string {
  const trimmed = (name || '').trim();
  if (!trimmed) return '';
  return canonicalizeSpeakerDisplayName(trimmed) || trimmed;
}

function findParticipant(
  name: string,
  participants: ProtocolParticipant[],
): ProtocolParticipant | undefined {
  if (!name?.trim()) return undefined;
  const display = displaySpeakerName(name);
  const direct = participants.find(p => (
    speakerNamesMatch(p.name, display)
    || speakerNamesMatch(p.name, name)
  ));
  if (direct) return direct;
  const key = normalizeNameKey(display || name);
  const surname = key.split(' ')[0];
  return participants.find(p => {
    const pk = normalizeNameKey(p.name);
    return pk === key || pk.startsWith(surname) || key.startsWith(pk.split(' ')[0]);
  });
}

function resolveParticipantPosition(
  p: ProtocolParticipant,
  speeches: ProtocolSpeech[],
): string {
  if (p.role && isJobTitleRole(p.role)) return p.role;
  if (p.role && !isBadgeRoleLabel(p.role) && !BADGE_ONLY.has(p.role.toLowerCase())) {
    return p.role;
  }
  const speech = speeches.find(s => speakerNamesMatch(s.title, p.name));
  if (speech?.position) return speech.position;
  if (speech?.role && isJobTitleRole(speech.role)) return speech.role;
  const resolved = resolveSpeakerRole(p.name, p.badgeRole || p.role);
  if (resolved.orgHint && isJobTitleRole(resolved.orgHint)) return resolved.orgHint;
  if (resolved.orgHint && resolved.orgHint.length > 8 && !isBadgeRoleLabel(resolved.orgHint)) {
    return resolved.orgHint;
  }
  return '—';
}

function findChair(parsed: ParsedProtocol): { title: string; name: string } {
  const chairSig = displaySpeakerName(parsed.signatures.chair?.trim()) || parsed.signatures.chair?.trim();
  if (chairSig && !isPlaceholderText(chairSig)) {
    const p = findParticipant(chairSig, parsed.participants);
    const title = p ? resolveParticipantPosition(p, parsed.speeches) : '';
    return { title: title !== '—' ? title : '', name: chairSig };
  }

  const chairSpeech = parsed.speeches.find(s => {
    const blob = `${s.title} ${s.position} ${s.role}`.toLowerCase();
    return blob.includes('председател') || /открыл совещание|открывает совещание/i.test(s.body);
  });

  const leader = parsed.participants.find(p => {
    const blob = `${p.role} ${p.name} ${p.badgeRole || ''}`.toLowerCase();
    return p.badgeRole?.toLowerCase().includes('руководител')
      || blob.includes('коммерческ')
      || blob.includes('председател')
      || blob.includes('и.о. директор');
  });

  const pick = leader || (chairSpeech ? findParticipant(chairSpeech.title, parsed.participants) : undefined);
  if (pick) {
    const title = resolveParticipantPosition(pick, parsed.speeches);
    return { title: title !== '—' ? title : pick.role || '', name: displaySpeakerName(pick.name) || pick.name };
  }

  if (parsed.participants[0]) {
    const p = parsed.participants[0];
    const title = resolveParticipantPosition(p, parsed.speeches);
    return { title: title !== '—' ? title : '', name: displaySpeakerName(p.name) || p.name };
  }
  return { title: '', name: '' };
}

function buildAssigneeHeader(assignee: string, participant?: ProtocolParticipant, speeches?: ProtocolSpeech[]): string {
  const label = displaySpeakerName(assignee) || assignee;
  if (!label || label === '—') {
    return 'Общие поручения';
  }
  if (participant) {
    const pos = resolveParticipantPosition(participant, speeches || []);
    const person = displaySpeakerName(participant.name) || participant.name;
    if (pos && pos !== '—') return `${pos} ${person}`;
    return person;
  }
  return label;
}

function groupDecisions(parsed: ParsedProtocol): { header: string; items: string[] }[] {
  const map = new Map<string, { header: string; items: string[] }>();

  parsed.tasks.forEach(t => {
    const task = t.task?.trim();
    if (!task) return;
    const rawAssignee = displaySpeakerName((t.assignee || '').trim()) || (t.assignee || '').trim();
    const isUnassigned = !rawAssignee || rawAssignee === '—' || /^участник$/i.test(rawAssignee);
    const participant = !isUnassigned ? findParticipant(rawAssignee, parsed.participants) : undefined;
    const key = participant?.name || (isUnassigned ? '__unassigned__' : rawAssignee);
    const header = buildAssigneeHeader(rawAssignee, participant, parsed.speeches);

    if (!map.has(key)) {
      map.set(key, { header, items: [] });
    }
    map.get(key)!.items.push(task);
  });

  const groups = Array.from(map.values());
  const unassigned = groups.filter(g => g.header === 'Общие поручения');
  const assigned = groups.filter(g => g.header !== 'Общие поручения');

  assigned.sort((a, b) => {
    const ia = parsed.participants.findIndex(p => a.header.includes(p.name));
    const ib = parsed.participants.findIndex(p => b.header.includes(p.name));
    return (ia < 0 ? 999 : ia) - (ib < 0 ? 999 : ib);
  });

  return [...assigned, ...unassigned];
}

function normalizeAgendaItem(item: string): string {
  let text = item.replace(/^\s*\d+[\.\)]\s*/, '').replace(/^\s*[-*•]\s*/, '').trim();
  if (text && !/[.!?…]$/.test(text)) text += '.';
  return text;
}

export function buildKtzhProtocolDocument(
  protocol: string,
  meetingName: string,
  date?: string,
): KtzhProtocolDocument {
  const parsed = parseProtocolMarkdown(protocol);
  const chair = findChair(parsed);

  const dateCandidates = [
    parsed.meta['Дата'],
    date,
    parsed.signatures.date,
  ].filter(Boolean) as string[];
  let dateLabel = '';
  for (const candidate of dateCandidates) {
    dateLabel = formatDateKtzh(candidate);
    if (dateLabel) break;
  }

  const title = (parsed.meta['Название встречи'] || meetingName || parsed.title || '').trim();
  const keyParticipants = parsed.participants
    .slice(0, 4)
    .map(p => shortName(displaySpeakerName(p.name) || p.name))
    .filter(Boolean);
  const topicLine = title
    ? (keyParticipants.length ? `${title} (${keyParticipants.join(', ')})` : title)
    : keyParticipants.join(', ');

  const established = (parsed.agenda.length
    ? parsed.agenda
    : (parsed.rawSections['2. Повестка дня'] || '')
      .split('\n')
      .map(l => l.replace(/^\s*\d+[\.\)]\s*/, '').replace(/^\s*[-*•]\s*/, '').trim())
      .filter(Boolean)
  ).map(normalizeAgendaItem);

  const participants = parsed.participants.map((p, i) => ({
    num: i + 1,
    name: displaySpeakerName(p.name) || p.name,
    position: resolveParticipantPosition(p, parsed.speeches),
  }));

  const chairFromTable = findParticipant(chair.name, parsed.participants);
  const signatureTitle = chairFromTable
    ? resolveParticipantPosition(chairFromTable, parsed.speeches)
    : chair.title;
  const signatureName = chair.name;

  return {
    meetingTitle: title,
    chairTitle: signatureTitle !== '—' ? signatureTitle : chair.title,
    chairName: chair.name,
    city: 'Астана',
    protocolNumber: extractProtocolNumber(protocol, parsed),
    dateLabel,
    topicLine,
    established,
    decisionGroups: groupDecisions(parsed),
    signatureTitle: signatureTitle !== '—' ? signatureTitle : chair.title,
    signatureName,
    participants,
  };
}
